// THE PRIZE WHEEL'S DETERMINISTIC SPIN — the same maths the app plays
// (mobile lib/utils/prize_wheel_spin.dart, itself the port of the reference
// wheel3d/wheel_preview.src.html: plan / spinAngle / rotorAngleAt). Every
// client plays the SAME motion from the spin object alone
// ({ startAt, roundDurationMs, gapMs, rounds, participantCount, seedHash })
// and the SERVER's clock, so the portal's livestream view and every phone
// show the wheel at the same angle at the same moment.
//
//   ANGLES: φ is the rotor's clockwise turn as seen from the front (the model:
//   WheelRotor.rotation.z = −φ). Participant i sits at θ_i = 2π (i + 0.5) / N
//   clockwise from 12 o'clock; it is under the pointer when wrap(θ_i + φ) = 0.
//   IDLE: one slow turn per IDLE_PERIOD_MS of server time.
//   A ROUND: from φ0 (the idle angle at round 1's start, else the previous
//   stop) 5–7 whole turns (a byte of the seed hash) + the way to the winner,
//   a 1.6°–2.7° overshoot settling back exactly on it at the round's end.
//   Round r starts startAt + (r − 1)(roundDurationMs + gapMs).

const TAU = Math.PI * 2;
const DEG = Math.PI / 180;

export const IDLE_PERIOD_MS = 75000;
/** How long the last round's reveal plays before the wheel is "done". */
export const REVEAL_MS = 5200;
export const DEFAULT_ROUND_MS = 9000;
export const DEFAULT_GAP_MS = 2500;

export const wrap = (a) => ((a % TAU) + TAU) % TAU;
const smooth = (a, b, x) => {
  const u = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return u * u * (3 - 2 * u);
};

/** FNV-1a (32-bit). */
export function fnv(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/** The idle rotor angle at the server time `ts` (0 … 2π). */
export const idleAngle = (ts) => TAU * ((((ts % IDLE_PERIOD_MS) + IDLE_PERIOD_MS) % IDLE_PERIOD_MS) / IDLE_PERIOD_MS);

/** Participant i's angle of n (clockwise from 12 o'clock). */
export const pointTheta = (i, n) => (TAU * (i + 0.5)) / Math.max(1, n);

function seedBytes(seedHash, wheelId, round) {
  const h = String(seedHash || '').trim().toLowerCase();
  if (h.length >= 4 && /^[0-9a-f]+$/.test(h)) {
    const slots = Math.floor((h.length - 4) / 2) + 1;
    const o = (((round - 1) * 2) % slots) * 2;
    return [parseInt(h.slice(o, o + 2), 16), parseInt(h.slice(o + 2, o + 4), 16)];
  }
  const f = fnv(`${wheelId}:${round}`);
  return [f & 0xff, (f >>> 8) & 0xff];
}

/**
 * The spin object (API `spin`) → a normalised spin, or null when it cannot be
 * played (no participants / no valid round).
 */
export function normalizeSpin(raw, wheelId = null) {
  if (!raw || typeof raw !== 'object') return null;
  const n = Number(raw.participantCount) || 0;
  const startAt = Number(raw.startAtMs ?? (raw.startAt ? Date.parse(raw.startAt) : NaN));
  if (n <= 0 || !Number.isFinite(startAt)) return null;
  const rounds = (Array.isArray(raw.rounds) ? raw.rounds : [])
    .map((r, i) => ({ round: Number(r.round ?? i + 1), winnerIndex: Number(r.winnerIndex), winner: r.winner ?? null }))
    .filter((r) => Number.isInteger(r.winnerIndex) && r.winnerIndex >= 0 && r.winnerIndex < n)
    .sort((a, b) => a.round - b.round);
  if (!rounds.length) return null;
  const dur = Number(raw.roundDurationMs);
  const gap = Number(raw.gapMs);
  return {
    wheelId: raw.wheelId ?? wheelId,
    participantCount: n,
    rounds,
    startAt,
    roundDurationMs: dur > 0 ? dur : DEFAULT_ROUND_MS,
    gapMs: gap >= 0 ? gap : DEFAULT_GAP_MS,
    seedHash: raw.seedHash ?? '',
  };
}

export const roundStart = (spin, k) => spin.startAt + (k - 1) * (spin.roundDurationMs + spin.gapMs);
export const spinEnd = (spin) => roundStart(spin, spin.rounds.length) + spin.roundDurationMs;

/** The rounds' motion (pure: only the spin). */
export function planSpin(spin) {
  if (!spin) return [];
  const out = [];
  let prevEnd = null;
  const n = spin.participantCount;
  spin.rounds.forEach((r, i) => {
    const k = i + 1;
    const start = roundStart(spin, k);
    const phi0 = prevEnd ?? idleAngle(start);
    const [b0, b1] = seedBytes(spin.seedHash, spin.wheelId, k);
    const turns = 5 + (b0 % 3);
    const ov = (1.6 + (b1 % 12) / 10) * DEG;
    const delta = turns * TAU + wrap(-pointTheta(r.winnerIndex, n) - phi0);
    const p = { round: k, winnerIndex: r.winnerIndex, n, startAt: start, durationMs: spin.roundDurationMs, endAt: start + spin.roundDurationMs, phi0, phiEnd: phi0 + delta, delta, ov, turns };
    out.push(p);
    prevEnd = p.phiEnd;
  });
  return out;
}

/** The rotor's angle in round p at the server time ts. */
export function spinAngle(p, ts) {
  const u = Math.min(1, Math.max(0, (ts - p.startAt) / p.durationMs));
  const split = 0.86;
  if (u < split) {
    const x = u / split;
    return p.phi0 + (p.delta + p.ov) * (1 - Math.pow(1 - x, 3.2)) * smooth(0, 0.1, x);
  }
  const w = (u - split) / (1 - split);
  return p.phiEnd + p.ov * Math.cos(1.5 * Math.PI * w) * (1 - w) * (1 - w) * (1 + 2 * w);
}

/** The rotor's angle at ts: idle without a plan (or before it), the current round's, or the last stop's. */
export function rotorAngle(plan, ts) {
  if (!plan.length || ts < plan[0].startAt) return idleAngle(ts);
  let phi = plan[0].phi0;
  for (const p of plan) {
    if (ts < p.startAt) break;
    phi = spinAngle(p, ts);
  }
  return phi;
}

/**
 * The draw at the server time ts:
 *   { phase: idle | armed | spinning | reveal | done, round (plan entry|null),
 *     sinceStopMs, revealed: winner indexes so far, omega (rad/s), untilStartMs }
 */
export function momentAt(plan, ts) {
  if (!plan.length) return { phase: 'idle', round: null, sinceStopMs: 0, revealed: [], omega: 0, untilStartMs: 0 };
  if (ts < plan[0].startAt) {
    return { phase: 'armed', round: null, sinceStopMs: 0, revealed: [], omega: 0, untilStartMs: plan[0].startAt - ts };
  }
  let i = 0;
  plan.forEach((p, k) => {
    if (ts >= p.startAt) i = k;
  });
  const cur = plan[i];
  if (ts < cur.endAt) {
    const omega = Math.abs(spinAngle(cur, ts + 16) - spinAngle(cur, ts)) / 0.016;
    return { phase: 'spinning', round: cur, sinceStopMs: 0, revealed: plan.slice(0, i).map((p) => p.winnerIndex), omega, untilStartMs: 0 };
  }
  const since = ts - cur.endAt;
  const last = i === plan.length - 1;
  return {
    phase: last && since >= REVEAL_MS ? 'done' : 'reveal',
    round: cur,
    sinceStopMs: since,
    revealed: plan.slice(0, i + 1).map((p) => p.winnerIndex),
    omega: 0,
    untilStartMs: 0,
  };
}
